import { DataLayer } from '@piwikpro/react-piwik-pro';
import React from 'react';
import { Parallax, ParallaxProvider } from 'react-scroll-parallax';

import { Link } from 'gatsby';

import {
  Button,
  buttonClasses,
  Grid2 as Grid,
  styled,
  SxProps,
  Theme,
} from '@mui/material';

import { ResponsiveGrid } from '@mewa/ui';

import Img, { ImageData } from '../../components/image';
import { PageHeadlineCTAProps } from '../../components/page-headline-wrapper';

// distance between heading and intro text view in percent of viewport height
const viewDistance = 90;

type IndustryHeroScrollerProps = {
  title: string;
  subtitle: string;
  cta?: PageHeadlineCTAProps;
  subClaim: string;
  introText: string;
  backgroundImage: ImageData[];
  imageIsForSearchIndex?: boolean;
};

export const IndustryHeroScroller = ({
  title,
  subtitle,
  cta,
  subClaim,
  introText,
  backgroundImage,
  imageIsForSearchIndex = false,
}: IndustryHeroScrollerProps) => {
  return (
    <ParallaxProvider>
      <div
        style={{
          position: 'relative',
          height: `${100 + viewDistance + 50}vh`,
        }}
      >
        <StickyContainer
          style={{
            position: 'sticky',
            zIndex: -1,
            top: 0,
            height: '100vh',
            marginTop: 0,
          }}
        >
          <Img
            alt={title}
            images={backgroundImage}
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              height: '100%',
              width: '100%',
              objectFit: 'cover',
              zIndex: -1,
              img: {
                objectPosition: 'top',
              },
            }}
            intendedForSearchIndex={imageIsForSearchIndex}
          />
          <GradientOverlay />
          <Parallax
            opacity={[0, 0.4]}
            startScroll={0}
            endScroll={
              typeof window !== 'undefined' && window.innerHeight
                ? window.innerHeight
                : 1000
            }
            style={{
              position: 'absolute',
              top: '0',
              height: '100vh',
              width: '100%',
            }}
          >
            <OpacityOverlay />
          </Parallax>
        </StickyContainer>

        <div
          style={{
            position: 'absolute',
            width: '100%',
            top: 0,
            height: '100vh',
          }}
        >
          <HeaderContainer>
            <Header>
              {title}
              <br />
              <Subtitle>{subtitle}</Subtitle>
            </Header>
          </HeaderContainer>

          <DescriptionContainer>
            <ResponsiveGrid>
              <Grid
                size={{
                  xs: 10,
                  md: 4,
                }}
              >
                <SubClaim>{subClaim}</SubClaim>
              </Grid>
              <Grid
                size={{
                  xs: 10,
                  md: 7,
                  lg: 6,
                }}
              >
                <IntroText>{introText}</IntroText>
                {cta && (
                  <WhiteButton
                    cta={cta}
                    sx={{ display: { xs: 'none', sm: 'inline-block' } }}
                  />
                )}
              </Grid>
              {cta && (
                <Grid sx={{ display: { xs: 'block', sm: 'none' } }} size={12}>
                  <WhiteButton cta={cta} />
                </Grid>
              )}
            </ResponsiveGrid>
          </DescriptionContainer>
        </div>
      </div>
    </ParallaxProvider>
  );
};

const WhiteButton = ({
  cta,
  sx,
}: {
  cta: PageHeadlineCTAProps;
  sx?: SxProps<Theme>;
}) => (
  <Button
    variant="outlined"
    component={Link}
    to={cta.link}
    sx={{
      ...sx,
      marginTop: { xs: 2, sm: 4, xl: 5 },
      width: { xs: '100%', sm: 'auto' },
      color: 'var(--color-white)',

      [`&.${buttonClasses.outlined}:not(:hover)`]: {
        borderColor: 'var(--color-white)',
      },
    }}
    onClick={() => {
      DataLayer.push({ event: 'cta_contact_hero_stage' });
    }}
  >
    {cta.label}
  </Button>
);

const SubClaim = styled('h2')(({ theme }) => ({
  ...theme.typography.h6,
  fontWeight: theme.typography.fontWeightMedium,
  margin: '0',

  [theme.breakpoints.up('sm')]: {
    fontWeight: theme.typography.fontWeightRegular,
    margin: '19px 0 0 0',
  },
}));

const IntroText = styled('p')(({ theme }) => ({
  ...theme.typography.h3,
  fontWeight: theme.typography.fontWeightRegular,
  marginTop: '22px',

  [theme.breakpoints.up('sm')]: {
    margin: '0',
  },
}));

const OpacityOverlay = styled('div')({
  position: 'absolute',
  top: '0',
  left: '0',
  height: '100%',
  width: '100%',
  backgroundColor: 'var(--color-dark-coal)',
});

const GradientOverlay = styled('div')({
  position: 'absolute',
  top: '0',
  left: '0',
  height: '100%',
  width: '100%',
  background:
    'linear-gradient(90deg, #222222 0%, rgba(196, 196, 196, 0) 77.55%)',
  opacity: '0.4',
});

const StickyContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '0',
  left: '0',
  height: '100%',
  [theme.breakpoints.up('sm')]: {},
}));

const DescriptionContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  // position in middle of viewport under the fold
  top: `${100 + viewDistance}vh`,

  transform: 'translateY(-50%)',
  height: 'fit-content',
  color: 'var(--color-white)',

  [theme.breakpoints.only('xs')]: {
    top: `${100 + viewDistance - 40}vh`,
  },
}));

const HeaderContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: '150px',
  left: 'var(--margin)',
  // leave 10% to the right as whitespace for text
  right: 'calc(10% + var(--margin))',
  color: 'var(--color-white)',

  [theme.breakpoints.up('sm')]: {
    bottom: 'var(--margin)',
  },
}));

const Header = styled('h1')(({ theme }) => ({
  ...theme.typography.h2,
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '130%',
  margin: '0',
  [theme.breakpoints.down('sm')]: {
    ...theme.typography.h6,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const Subtitle = styled('span')(({ theme }) => ({
  ...theme.typography.h2,
  fontWeight: theme.typography.fontWeightLight,
  lineHeight: '130%',
  margin: '0',
  [theme.breakpoints.down('sm')]: {
    ...theme.typography.h6,
    fontWeight: theme.typography.fontWeightLight,
  },
}));
